import { useState } from 'react'
import { useFixarSetor } from '../../api/queries/repositorios'
import { Botao } from '../../components/Botao'
import { CampoDeSelecao, CampoDeTexto, CampoDeTextoLongo } from '../../components/campos'
import { Dialogo } from '../../components/Dialogo'
import { AvisoDeErro } from '../../components/Estados'
import { TEXTOS } from '../../lib/glossario'
import type { SetorDoAutor } from '../../types/repositorios'
import { ORDEM_DOS_SETORES, rotuloDoSetor } from './setores'

interface Props {
  autor: { id: number; nome: string; email: string; setor: SetorDoAutor }
  aoFechar: () => void
}

/**
 * Corrigir o setor de quem fez o commit — o ato do fiscal sobre a regra do e-mail.
 *
 * A regra deduz o setor do domínio e erra nos casos que ela não conhece (e-mail pessoal de gente
 * da contratada, conta institucional sem órgão). O que se fixa aqui fica com o nome de quem fixou,
 * e a regra não sobrescreve mais.
 *
 * @param props o autor e o que fazer ao fechar
 * @returns o diálogo
 */
export function DialogoDeSetor({ autor, aoFechar }: Props) {
  const t = TEXTOS.repositorios.pessoas.dialogo
  const [setor, setSetor] = useState<SetorDoAutor>(autor.setor)
  const [nomeDeExibicao, setNomeDeExibicao] = useState('')
  const [observacao, setObservacao] = useState('')
  const fixar = useFixarSetor()

  function salvar() {
    fixar.mutate({ id: autor.id, setor, nomeDeExibicao, observacao }, { onSuccess: aoFechar })
  }

  return (
    <Dialogo
      titulo={t.titulo}
      descricao={`${autor.nome} · ${autor.email}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao aparencia="secundario" onClick={aoFechar} disabled={fixar.isPending}>
            {t.cancelar}
          </Botao>
          <Botao aparencia="primario" onClick={salvar} disabled={fixar.isPending}>
            {t.salvar}
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">{t.ajuda}</p>
        <CampoDeSelecao rotulo={t.setor} value={setor} onChange={(evento) => setSetor(evento.target.value as SetorDoAutor)}>
          {ORDEM_DOS_SETORES.map((opcao) => (
            <option key={opcao} value={opcao}>
              {rotuloDoSetor(opcao)}
            </option>
          ))}
        </CampoDeSelecao>
        <CampoDeTexto
          rotulo={t.nomeDeExibicao}
          ajuda={t.nomeDeExibicaoAjuda}
          value={nomeDeExibicao}
          onChange={(evento) => setNomeDeExibicao(evento.target.value)}
          maxLength={200}
        />
        <CampoDeTextoLongo
          rotulo={t.observacao}
          ajuda={t.observacaoAjuda}
          value={observacao}
          onChange={(evento) => setObservacao(evento.target.value)}
          rows={3}
          maxLength={1000}
        />
        {fixar.isError && <AvisoDeErro erro={fixar.error} />}
      </div>
    </Dialogo>
  )
}
